import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { AuditLog, AuditLogFilter, getAuditDisplayText, useGetAuditLogsQuery } from "../../api/auditApi";
import {
  Action,
  Column,
  DataTable,
} from "../../components/common/DataTable/DataTable";
import { AuditIcon } from "../../components/common/pageIcons";
import { SearchableColumn } from "../../components/common/SearchInputs";
import PageTitle from "../../components/common/PageTitle";
import PageLayout from "../../components/layout/PageLayout";
import { useInfiniteDataTableItems } from "../../hooks/useInfiniteDataTableItems";
import AuditActionChip from "./components/AuditActionChip";
import AuditDetailsDialog from "./components/AuditDetailsDialog";

const ENTITY_TYPES = [
  "Vehicle",
  "Trailer",
  "Employee",
  "VehicleAssignment",
  "TrailerAssignment",
  "DriverDispatcherAssignment",
];

const ACTIONS = [
  "Created",
  "Updated",
  "Deleted",
  "VehicleAssigned",
  "VehicleUnassigned",
  "TrailerAssigned",
  "TrailerUnassigned",
  "DispatcherAssigned",
  "DispatcherUnassigned",
  "EmployeeCreated",
  "EmployeeUpdated",
  "EmployeeDeleted",
  "EmployeeProfileUpdated",
];

const formatDateTime = (value: string) => new Date(value).toLocaleString();
const AUDIT_LOG_PAGE_SIZE = 50;
const ISO_DATE_RANGE_PATTERN = /^(\d{4}-\d{2}-\d{2})-(\d{4}-\d{2}-\d{2})$/;

const parseNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const AuditPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [searchFilters, setSearchFilters] = useState<Record<string, any>>({});
  const [selectedEntityType, setSelectedEntityType] = useState("");
  const [selectedAuditLog, setSelectedAuditLog] = useState<AuditLog | null>(null);

  const dateRange = useMemo(() => {
    const match = String(searchFilters.createdAt ?? "").match(ISO_DATE_RANGE_PATTERN);
    return match ? { from: match[1], to: `${match[2]}T23:59:59` } : {};
  }, [searchFilters.createdAt]);

  const queryFilters = useMemo<AuditLogFilter>(() => {
    return {
      entityType: selectedEntityType || undefined,
      entityId: parseNumber(searchFilters.entityId),
      actorUserId: parseNumber(searchFilters.actorUserId),
      action: searchFilters.action || undefined,
      from: dateRange.from,
      to: dateRange.to,
      page: page + 1,
      pageSize: AUDIT_LOG_PAGE_SIZE,
    };
  }, [dateRange.from, dateRange.to, page, searchFilters, selectedEntityType]);

  const { currentData, isFetching, isLoading } = useGetAuditLogsQuery(queryFilters);
  const { accumulatedItems, resetAccumulatedItems } = useInfiniteDataTableItems(
    currentData?.data,
    page
  );

  const columns = useMemo<Column[]>(
    () => [
      {
        field: "createdAt",
        headerName: "Date",
        width: 180,
        renderCell: (value: string) => (
          <Typography sx={{ fontSize: "0.8125rem" }}>
            {formatDateTime(value)}
          </Typography>
        ),
      },
      {
        field: "entityType",
        headerName: "Entity Type",
        width: 170,
      },
      {
        field: "entityId",
        headerName: "Entity ID",
        width: 120,
      },
      {
        field: "action",
        headerName: "Action",
        width: 190,
        renderCell: (value: string) => <AuditActionChip action={value} />,
      },
      {
        field: "actorUserId",
        headerName: "Actor",
        minWidth: 180,
        flex: 1,
        renderCell: (_: number | null, row: AuditLog) =>
          row.actorName ||
          row.actorEmail ||
          (row.actorUserId ? `User #${row.actorUserId}` : "System"),
      },
      {
        field: "details",
        headerName: "Details",
        minWidth: 240,
        flex: 1.6,
        maxWidth: 460,
        renderCell: (_: string | null, row: AuditLog) => getAuditDisplayText(row),
      },
    ],
    []
  );

  const actions = useMemo<Action<AuditLog>[]>(
    () => [
      {
        icon: <VisibilityIcon fontSize="small" />,
        label: "View Details",
        onClick: (auditLog) => setSelectedAuditLog(auditLog),
        color: "primary",
      },
    ],
    []
  );

  const searchableColumns = useMemo<SearchableColumn[]>(
    () => [
      {
        field: "createdAt",
        headerName: "Date",
        searchable: true,
        searchType: "date",
      },
      {
        field: "entityId",
        headerName: "Entity ID",
        searchable: true,
        searchType: "number",
      },
      {
        field: "action",
        headerName: "Action",
        searchable: true,
        searchType: "select",
        selectOptions: ACTIONS.map((action) => ({
          value: action,
          label: action,
        })),
      },
      {
        field: "actorUserId",
        headerName: "Actor User ID",
        searchable: true,
        searchType: "number",
      },
    ],
    []
  );

  const handleSearchChange = useCallback(
    (filters: Record<string, any>) => {
      setSearchFilters(filters);
      resetAccumulatedItems();
      setPage(0);
    },
    [resetAccumulatedItems]
  );

  const handleEntityTypeChange = useCallback(
    (_: React.SyntheticEvent, nextEntityType: string) => {
      setSelectedEntityType(nextEntityType);
      resetAccumulatedItems();
      setPage(0);
    },
    [resetAccumulatedItems]
  );

  const handleLoadMore = useCallback(() => {
    if (currentData?.hasNext && !isFetching) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [currentData?.hasNext, isFetching]);

  return (
    <PageLayout variant="fullHeight">
      <PageTitle title="Audit Log" icon={<AuditIcon />} />
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
        }}
      >
        <Tabs
          value={selectedEntityType}
          onChange={handleEntityTypeChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Audit log entity type filters"
        >
          <Tab label="All" value="" />
          {ENTITY_TYPES.map((entityType) => (
            <Tab key={entityType} label={entityType} value={entityType} />
          ))}
        </Tabs>
      </Box>
      <DataTable<AuditLog>
        items={accumulatedItems}
        columns={columns}
        actions={actions}
        isLoading={isLoading && accumulatedItems.length === 0}
        isLoadingRows={isFetching && page === 0 && accumulatedItems.length === 0}
        isLoadingMore={isFetching && page > 0}
        title={selectedEntityType ? `${selectedEntityType} Audit Actions` : "All Audit Actions"}
        totalCount={currentData?.totalRecords ?? 0}
        hasNextPage={currentData?.hasNext}
        onLoadMore={handleLoadMore}
        enableSearch
        searchableColumns={searchableColumns}
        onSearchChange={handleSearchChange}
        getRowId={(row) => row.id}
      />
      <AuditDetailsDialog
        open={Boolean(selectedAuditLog)}
        auditLog={selectedAuditLog}
        onClose={() => setSelectedAuditLog(null)}
      />
    </PageLayout>
  );
};

export default AuditPage;
